import React, { useEffect, useRef, useState } from 'react';
import { Button, Flex, Image, Input, Popconfirm, Upload, Typography, theme } from 'antd';
import type { TextAreaRef } from 'antd/es/input/TextArea';
import {
    CheckOutlined,
    CloseOutlined,
    DeleteOutlined,
    EditOutlined,
    PaperClipOutlined,
    SendOutlined,
} from '@ant-design/icons';
import TgAvatar from './TgAvatar';
import type { Task, User } from '../../types';
import {
    useAddTaskCommentMutation,
    useUpdateTaskCommentMutation,
    useDeleteTaskCommentMutation,
    useAddTaskCommentImageMutation,
    useDeleteTaskCommentImageMutation,
} from '../store/tasks';
import { useGetUserDataQuery } from '../store/auth';
import { MAX_COMMENT_IMAGES } from '../constants';
import CommentEmojiPicker from './CommentEmojiPicker';
import CommentGiphyPicker from './CommentGiphyPicker';
import { getNotificationInstance } from '../utils/notification';

const { Text } = Typography;

interface TaskCommentsProps {
    task: Task;
}

type PendingFile = {
    id: string;
    file: File;
    previewUrl: string;
};

const formatCommentDate = (value?: string | null) => {
    if (!value) {
        return '';
    }
    return new Date(value).toLocaleString([], {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
};

const displayName = (user: User) => (
    user.lastName ? `${user.firstName} ${user.lastName}` : user.firstName
);

const resolveImageSrc = (imagePath: string) => {
    if (!imagePath) {
        return '';
    }
    if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
        return imagePath;
    }

    let normalized = imagePath.replace(/\\/g, '/');
    if (normalized.startsWith('./')) {
        normalized = normalized.slice(1);
    }
    if (!normalized.startsWith('/')) {
        normalized = `/${normalized}`;
    }

    if (normalized.startsWith('/api/public/')) {
        return encodeURI(normalized);
    }
    if (normalized.startsWith('/public/')) {
        return encodeURI(`/api${normalized}`);
    }
    return encodeURI(normalized);
};

const TaskComments: React.FC<TaskCommentsProps> = ({ task }) => {
    const { token } = theme.useToken();
    const { colorText, colorTextSecondary, colorBorderSecondary, colorFillQuaternary } = token;
    const [ text, setText ] = useState('');
    const textRef = useRef<TextAreaRef>(null);
    const [ pendingFiles, setPendingFiles ] = useState<PendingFile[]>([]);
    const pendingFilesRef = useRef<PendingFile[]>([]);
    const [ editingId, setEditingId ] = useState<number | null>(null);
    const [ editText, setEditText ] = useState('');
    const { data: currentUser } = useGetUserDataQuery();
    const [ addComment, { isLoading: isSending } ] = useAddTaskCommentMutation();
    const [ updateComment, { isLoading: isUpdating } ] = useUpdateTaskCommentMutation();
    const [ deleteComment ] = useDeleteTaskCommentMutation();
    const [ addCommentImage ] = useAddTaskCommentImageMutation();
    const [ deleteCommentImage ] = useDeleteTaskCommentImageMutation();

    const comments = Array.isArray(task.comments) ? task.comments : [];

    useEffect(() => {
        pendingFilesRef.current = pendingFiles;
    }, [pendingFiles]);

    useEffect(() => {
        return () => {
            pendingFilesRef.current.forEach((item) => URL.revokeObjectURL(item.previewUrl));
        };
    }, []);

    const clearPendingFiles = () => {
        pendingFiles.forEach((item) => URL.revokeObjectURL(item.previewUrl));
        setPendingFiles([]);
    };

    const handleAttach = (file: File) => {
        const notification = getNotificationInstance();
        if (!file.type.startsWith('image/')) {
            notification?.error({
                message: 'Ошибка',
                description: 'Можно прикрепить только изображение или GIF',
            });
            return false;
        }
        if (file.size / 1024 / 1024 > 10) {
            notification?.error({
                message: 'Ошибка',
                description: 'Размер файла не должен превышать 10MB',
            });
            return false;
        }
        if (pendingFiles.length >= MAX_COMMENT_IMAGES) {
            notification?.error({
                message: 'Ошибка',
                description: `К комментарию можно прикрепить не больше ${MAX_COMMENT_IMAGES} изображений`,
            });
            return false;
        }

        setPendingFiles((prev) => [
            ...prev,
            {
                id: `${file.name}-${file.size}-${Date.now()}`,
                file,
                previewUrl: URL.createObjectURL(file),
            },
        ]);
        return false;
    };

    const removePendingFile = (id: string) => {
        setPendingFiles((prev) => {
            const target = prev.find((item) => item.id === id);
            if (target) {
                URL.revokeObjectURL(target.previewUrl);
            }
            return prev.filter((item) => item.id !== id);
        });
    };

    const handleSend = async () => {
        const comment = text.trim();
        if ((!comment && pendingFiles.length === 0) || isSending) {
            return;
        }

        try {
            await addComment({
                todoId: task.todoId,
                taskId: task.taskId,
                comment,
                files: pendingFiles.map((item) => item.file),
            }).unwrap();
            setText('');
            clearPendingFiles();
        } catch {
            // toast already shown
        }
    };

    const insertEmoji = (emoji: string) => {
        const el = textRef.current?.resizableTextArea?.textArea;
        if (!el) {
            setText((prev) => `${prev}${emoji}`);
            return;
        }

        const start = el.selectionStart ?? el.value.length;
        const end = el.selectionEnd ?? start;
        const next = `${el.value.slice(0, start)}${emoji}${el.value.slice(end)}`;
        setText(next);

        requestAnimationFrame(() => {
            el.focus();
            const pos = start + emoji.length;
            el.setSelectionRange(pos, pos);
        });
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            handleSend();
        }
    };

    const startEdit = (commentId: number, comment: string) => {
        setEditingId(commentId);
        setEditText(comment || '');
    };

    const cancelEdit = () => {
        setEditingId(null);
        setEditText('');
    };

    const saveEdit = async (commentId: number) => {
        try {
            await updateComment({
                todoId: task.todoId,
                taskId: task.taskId,
                commentId,
                comment: editText.trim(),
            }).unwrap();
            cancelEdit();
        } catch {
            // toast
        }
    };

    return (
        <div className="task-comments">
            {comments.length === 0 ? (
                <div
                    className="task-comments__empty"
                    style={{ color: colorTextSecondary, background: colorFillQuaternary }}
                >
                    Пока нет комментариев — напишите первым
                </div>
            ) : (
                <div className="task-comments__list">
                    {comments.map((item) => {
                        const isOwner = currentUser?.userId != null
                            && Number(currentUser.userId) === Number(item.user?.userId);
                        const images = Array.isArray(item.images) ? item.images : [];
                        const isEditing = editingId === item.commentId;

                        return (
                            <div
                                key={item.commentId}
                                className="task-comments__item"
                                style={{ borderColor: colorBorderSecondary }}
                            >
                                <TgAvatar user={item.user} />
                                <div className="task-comments__body">
                                    <Flex align="center" justify="space-between" gap={8}>
                                        <Text strong style={{ color: colorText }}>
                                            {displayName(item.user)}
                                        </Text>
                                        <Flex align="center" gap={4}>
                                            <Text
                                                type="secondary"
                                                style={{ fontSize: 12, color: colorTextSecondary }}
                                            >
                                                {formatCommentDate(item.updatedAt || item.createDate)}
                                                {item.updatedAt ? ' · изм.' : ''}
                                            </Text>
                                            {isOwner && !isEditing && (
                                                <>
                                                    <Button
                                                        type="text"
                                                        size="small"
                                                        icon={<EditOutlined />}
                                                        aria-label="Редактировать комментарий"
                                                        onClick={() => startEdit(item.commentId, item.comment)}
                                                    />
                                                    <Popconfirm
                                                        title="Удалить комментарий?"
                                                        okText="Удалить"
                                                        cancelText="Отмена"
                                                        onConfirm={() => deleteComment({
                                                            todoId: task.todoId,
                                                            taskId: task.taskId,
                                                            commentId: item.commentId,
                                                        })}
                                                    >
                                                        <Button
                                                            type="text"
                                                            size="small"
                                                            danger
                                                            icon={<DeleteOutlined />}
                                                            aria-label="Удалить комментарий"
                                                        />
                                                    </Popconfirm>
                                                </>
                                            )}
                                        </Flex>
                                    </Flex>

                                    {isEditing ? (
                                        <div className="task-comments__edit">
                                            <Input.TextArea
                                                value={editText}
                                                onChange={(e) => setEditText(e.target.value)}
                                                autoSize={{ minRows: 2, maxRows: 6 }}
                                                maxLength={4000}
                                            />
                                            <Flex gap={8} style={{ marginTop: 8 }}>
                                                <Upload
                                                    accept="image/*"
                                                    showUploadList={false}
                                                    beforeUpload={(file) => {
                                                        if (images.length >= MAX_COMMENT_IMAGES) {
                                                            return false;
                                                        }
                                                        addCommentImage({
                                                            todoId: task.todoId,
                                                            taskId: task.taskId,
                                                            commentId: item.commentId,
                                                            file,
                                                        });
                                                        return false;
                                                    }}
                                                >
                                                    <Button
                                                        size="small"
                                                        icon={<PaperClipOutlined />}
                                                        disabled={images.length >= MAX_COMMENT_IMAGES}
                                                    >
                                                        Фото
                                                    </Button>
                                                </Upload>
                                                <Button
                                                    size="small"
                                                    type="primary"
                                                    icon={<CheckOutlined />}
                                                    loading={isUpdating}
                                                    onClick={() => saveEdit(item.commentId)}
                                                >
                                                    Сохранить
                                                </Button>
                                                <Button
                                                    size="small"
                                                    icon={<CloseOutlined />}
                                                    onClick={cancelEdit}
                                                >
                                                    Отмена
                                                </Button>
                                            </Flex>
                                        </div>
                                    ) : (
                                        item.comment ? (
                                            <Text
                                                className="task-comments__text"
                                                style={{ color: colorText, whiteSpace: 'pre-wrap' }}
                                            >
                                                {item.comment}
                                            </Text>
                                        ) : null
                                    )}

                                    {images.length > 0 && (
                                        <Image.PreviewGroup>
                                            <Flex wrap="wrap" gap={8} className="task-comments__images">
                                                {images.map((image) => (
                                                    <div key={image.imageId} className="task-comments__image">
                                                        <Image
                                                            src={resolveImageSrc(image.imagePath)}
                                                            width={88}
                                                            height={88}
                                                            style={{ objectFit: 'cover', borderRadius: 8 }}
                                                        />
                                                        {isOwner && isEditing && (
                                                            <Popconfirm
                                                                title="Удалить изображение?"
                                                                okText="Удалить"
                                                                cancelText="Отмена"
                                                                onConfirm={() => deleteCommentImage({
                                                                    todoId: task.todoId,
                                                                    taskId: task.taskId,
                                                                    commentId: item.commentId,
                                                                    imageId: image.imageId,
                                                                })}
                                                            >
                                                                <Button
                                                                    className="task-comments__image-remove"
                                                                    type="primary"
                                                                    danger
                                                                    size="small"
                                                                    shape="circle"
                                                                    icon={<CloseOutlined />}
                                                                />
                                                            </Popconfirm>
                                                        )}
                                                    </div>
                                                ))}
                                            </Flex>
                                        </Image.PreviewGroup>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {pendingFiles.length > 0 && (
                <Flex wrap="wrap" gap={8} className="task-comments__pending">
                    {pendingFiles.map((item) => (
                        <div key={item.id} className="task-comments__image">
                            <img src={item.previewUrl} alt="" />
                            <Button
                                className="task-comments__image-remove"
                                type="primary"
                                danger
                                size="small"
                                shape="circle"
                                icon={<CloseOutlined />}
                                onClick={() => removePendingFile(item.id)}
                            />
                        </div>
                    ))}
                </Flex>
            )}

            <div className="task-comments__composer">
                {currentUser && <TgAvatar user={currentUser} />}
                <div className="task-comments__field">
                    <Input.TextArea
                        ref={textRef}
                        value={text}
                        onChange={(e) => setText(e.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder="Написать комментарий…"
                        autoSize={{ minRows: 1, maxRows: 4 }}
                        maxLength={4000}
                    />
                    <CommentEmojiPicker onSelect={insertEmoji} />
                    <CommentGiphyPicker
                        onSelect={(file) => {
                            handleAttach(file);
                        }}
                        disabled={pendingFiles.length >= MAX_COMMENT_IMAGES}
                    />
                </div>
                <Upload
                    accept="image/*"
                    showUploadList={false}
                    beforeUpload={handleAttach}
                    disabled={pendingFiles.length >= MAX_COMMENT_IMAGES}
                >
                    <Button
                        type="text"
                        shape="circle"
                        icon={<PaperClipOutlined />}
                        disabled={pendingFiles.length >= MAX_COMMENT_IMAGES}
                        aria-label="Прикрепить изображение"
                    />
                </Upload>
                <Button
                    className="task-comments__send"
                    type="primary"
                    shape="circle"
                    icon={<SendOutlined />}
                    loading={isSending}
                    disabled={!text.trim() && pendingFiles.length === 0}
                    onClick={handleSend}
                    aria-label="Отправить комментарий"
                />
            </div>
        </div>
    );
};

export default TaskComments;
